import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft, FaChevronDown, FaTimes } from "react-icons/fa";
import axiosInstance from "../components/api/axiosInstance";
import { createTask, fetchTaskStatuses, fetchProjectUsers } from "./taskService";
import { fetchUserStories } from "./userStoryService";
import { extractCreatedTaskId, uploadTaskFile } from "./taskFileService";
import { canPerformActionOnPage, PAGES, ACTIONS } from "../utils/roleAccess";
import "../assets/css/EditTenant.css";
import "../assets/css/Projects.css";
import "../assets/css/UserStories.css";
import "../assets/css/Tasks.css";
import "../assets/css/Permissions.css";
import "../assets/css/Issues.css";
import { getAuthItem } from "../utils/authStorage";

function AddTask({ onBack, onTaskCreated }) {
  const navigate = useNavigate();
  const { id: projectId, subprojectId, userStoryId } = useParams();
  const role = useMemo(() => {
    try {
      const u = getAuthItem("user");
      if (!u) return null;
      const user = JSON.parse(u);
      const primaryRole = Array.isArray(user.roles) && user.roles.length > 0 ? user.roles[0] : null;
      return (
        (primaryRole && (primaryRole.role_name || primaryRole.name || primaryRole.role)) ||
        user.role_name ||
        user.role ||
        null
      );
    } catch {
      return null;
    }
  }, []);
  const canAddTaskFiles = canPerformActionOnPage(PAGES.PROJECTS, ACTIONS.ADD, role);
  const [submitting, setSubmitting] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [error, setError] = useState("");
  const [taskStatuses, setTaskStatuses] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [showAssigneesDropdown, setShowAssigneesDropdown] = useState(false);
  const [projects, setProjects] = useState([]);
  const [subprojects, setSubprojects] = useState([]);
  const [userStories, setUserStories] = useState([]);
  const [loadingSubprojects, setLoadingSubprojects] = useState(false);
  const [loadingUserStories, setLoadingUserStories] = useState(false);
  const [formData, setFormData] = useState({
    projectId: "",
    subProjectId: "",
    userStoryId: "",
    title: "",
    description: "",
    startDate: "",
    endDate: "",
    status: "",
    priority: "",
    assignedUserIds: []
  });
  const TASK_PRIORITIES = ["High", "Medium", "Low"];

  useEffect(() => {
    const load = async () => {
      try {
        const statuses = await fetchTaskStatuses();
        setTaskStatuses((Array.isArray(statuses) ? statuses : []).sort((a, b) => (a.Status_Id ?? a.status_id ?? 0) - (b.Status_Id ?? b.status_id ?? 0)));
      } catch (err) {
        console.error("Error fetching task statuses:", err);
      }
    };
    load();
  }, []);

  // Fetch projects
  useEffect(() => {
    const load = async () => {
      try {
        const res = await axiosInstance.get("/projects");
        const raw = res.data;
        const arr = Array.isArray(raw) ? raw : raw?.data ?? raw?.projects ?? [];
        setProjects(arr || []);
      } catch (err) {
        console.error("Error fetching projects:", err);
        setProjects([]);
      }
    };
    load();
  }, []);

  // Note: Don't auto-fill dropdowns from URL params.
  // This page should start with "Select ..." placeholders so user chooses explicitly.

  // Fetch subprojects when project changes
  useEffect(() => {
    if (!formData.projectId) {
      setSubprojects([]);
      return;
    }
    let cancelled = false;
    setLoadingSubprojects(true);
    axiosInstance
      .get(`/sub-projects/project/${formData.projectId}`)
      .then((res) => {
        if (cancelled) return;
        const raw = res.data;
        let arr = Array.isArray(raw) ? raw : raw?.sub_projects ?? raw?.data ?? [];
        const mapped = (arr || [])
          .map((sp) => ({
            id: sp.Sub_Project_Id ?? sp.sub_project_id ?? sp.id,
            name: sp.Sub_Project_Name ?? sp.sub_project_name ?? sp.name ?? ""
          }))
          .filter((x) => x.id != null && String(x.name).trim() !== "");
        setSubprojects(mapped);
        // If project has no subprojects, keep subProjectId empty.
        if (mapped.length === 0) {
          setFormData((f) => ({ ...f, subProjectId: "" }));
        }
      })
      .catch(() => { if (!cancelled) setSubprojects([]); })
      .finally(() => { if (!cancelled) setLoadingSubprojects(false); });
    return () => { cancelled = true; };
  }, [formData.projectId]);

  // Fetch user stories when project/subproject changes
  useEffect(() => {
    if (!formData.projectId) {
      setUserStories([]);
      return;
    }
    let cancelled = false;
    setLoadingUserStories(true);
    fetchUserStories(Number(formData.projectId), formData.subProjectId ? Number(formData.subProjectId) : null)
      .then((arr) => {
        if (!cancelled) {
          const mapped = (arr || []).map((s) => ({
            id: s.User_Story_Id ?? s.user_story_id ?? s.id,
            title: s.Title ?? s.title ?? ""
          })).filter((x) => x.id);
          setUserStories(mapped);
        }
      })
      .catch(() => { if (!cancelled) setUserStories([]); })
      .finally(() => { if (!cancelled) setLoadingUserStories(false); });
    return () => { cancelled = true; };
  }, [formData.projectId, formData.subProjectId]);

  // Fetch project users when project/subproject changes
  useEffect(() => {
    if (!formData.projectId) {
      setUsers([]);
      setLoadingUsers(false);
      return;
    }
    setLoadingUsers(true);
    fetchProjectUsers(formData.projectId, formData.subProjectId || null)
      .then((projectUsers) => {
        const mapped = projectUsers.map((u) => ({
          id: u.User_Id ?? u.user_id ?? u.id,
          name: (u.User_Name ?? u.user_name) || "—"
        })).filter((x) => x.id);
        setUsers(mapped);
      })
      .catch(() => setUsers([]))
      .finally(() => setLoadingUsers(false));
  }, [formData.projectId, formData.subProjectId]);

  const handleProjectChange = (e) => {
    const val = e.target.value;
    setFormData((f) => ({ ...f, projectId: val, subProjectId: "", userStoryId: "" }));
  };
  const handleSubprojectChange = (e) => {
    const val = e.target.value;
    setFormData((f) => ({ ...f, subProjectId: val, userStoryId: "" }));
  };

  // Only show Subproject field when the selected project has subprojects.
  // While fetching, keep it visible to avoid UI flicker.
  const showSubprojectField =
    Boolean(formData.projectId) && (loadingSubprojects ? true : subprojects.length > 0);

  const handleUserToggle = (userId) => {
    setFormData((prev) => {
      const ids = prev.assignedUserIds.includes(userId) ? prev.assignedUserIds.filter((id) => id !== userId) : [...prev.assignedUserIds, userId];
      return { ...prev, assignedUserIds: ids };
    });
  };

  const allUsersSelected = !loadingUsers && users.length > 0 && formData.assignedUserIds.length === users.length;
  const handleToggleSelectAllUsers = () => {
    setFormData((prev) => ({
      ...prev,
      assignedUserIds: allUsersSelected ? [] : users.map((u) => u.id)
    }));
  };

  useEffect(() => {
    const closeDropdown = (e) => {
      if (showAssigneesDropdown && !e.target.closest(".form-group-multi-select")) {
        setShowAssigneesDropdown(false);
      }
    };
    document.addEventListener("click", closeDropdown);
    return () => document.removeEventListener("click", closeDropdown);
  }, [showAssigneesDropdown]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const title = (formData.title || "").trim();
    if (!title) {
      setError("Title is required.");
      return;
    }
    if (!formData.projectId) {
      setError("Project is required.");
      return;
    }
    if (!formData.userStoryId) {
      setError("User Story is required.");
      return;
    }
    const todayStr = new Date().toISOString().split("T")[0];
    if (formData.startDate && formData.startDate < todayStr) {
      setError("Start date cannot be in the past.");
      return;
    }
    if (formData.endDate && formData.endDate < todayStr) {
      setError("End date cannot be in the past.");
      return;
    }
    if (formData.startDate && formData.endDate && formData.endDate < formData.startDate) {
      setError("End date cannot be before start date.");
      return;
    }
    setError("");
    let createdBy = 1;
    try {
      const userStr = getAuthItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        createdBy = user.user_id || user.id || 1;
      }
    } catch (err) {
      console.error("Error reading user:", err);
    }
    const payload = {
      Project_Id: Number(formData.projectId),
      Sub_Project_Id: formData.subProjectId ? Number(formData.subProjectId) : null,
      Story_Id: Number(formData.userStoryId),
      Title: title,
      Description: (formData.description || "").trim(),
      Start_Date: formData.startDate || null,
      End_Date: formData.endDate || null,
      Created_By: createdBy,
      Assigned_User_Ids: (formData.assignedUserIds || []).map((id) => Number(id))
    };

    setSubmitting(true);
    try {
      const created = await createTask(payload);
      const newTaskId =
        extractCreatedTaskId(created) ??
        (() => {
          const raw = created?.Task_Id ?? created?.task_id ?? created?.id;
          const n = Number(raw);
          return Number.isFinite(n) && n > 0 ? n : null;
        })();

      if (pendingFiles.length > 0) {
        if (newTaskId == null) {
          alert(
            "Task was created, but the response did not include a task id, so attachments were not uploaded. Add them from View or Edit task."
          );
          setPendingFiles([]);
        } else {
          for (const file of pendingFiles) {
            try {
              await uploadTaskFile(newTaskId, file);
            } catch (uploadErr) {
              console.error("Task file upload after create:", uploadErr);
              alert(
                uploadErr.response?.data?.message ||
                  uploadErr.response?.data?.error ||
                  "Task created, but a file failed to upload. Add files from the task page."
              );
              break;
            }
          }
          setPendingFiles([]);
        }
      }

      alert("Task created successfully.");
      if (onTaskCreated) onTaskCreated();
      if (onBack) onBack();
      else if (formData.subProjectId) {
        navigate(`/projects/${formData.projectId}/subprojects/${formData.subProjectId}/user-stories/${formData.userStoryId}/view`);
      } else {
        navigate(`/projects/${formData.projectId}/user-stories/${formData.userStoryId}/view`);
      }
    } catch (err) {
      console.error("Error creating task:", err);
      setError(
        err.response?.data?.message || err.response?.data?.error || "Failed to create task."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const today = new Date().toISOString().split("T")[0];

  const goBack = () => {
    if (onBack) onBack();
    else if (formData.subProjectId) {
      navigate(`/projects/${formData.projectId}/subprojects/${formData.subProjectId}/user-stories/${formData.userStoryId}/view`);
    } else if (formData.projectId && formData.userStoryId) {
      navigate(`/projects/${formData.projectId}/user-stories/${formData.userStoryId}/view`);
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="project-form-container edittenant-container">
      <div className="viewlead-header">
        <button type="button" className="viewlead-back-link" onClick={goBack}>
          <FaArrowLeft /> Back
        </button>
      </div>
      <div className="issues-main-card">
      <form className="edittenant-form edituserstory-form" onSubmit={handleSubmit}>
        <div className="form-section">

          {error && (
            <div className="userstories-add-error" style={{ marginBottom: 16 }}>
              {error}
            </div>
          )}
          <div className="form-grid addtask-form-grid">
            <div className="form-group">
              <label>Project <span className="required">*</span></label>
              <select
                name="projectId"
                value={formData.projectId}
                onChange={handleProjectChange}
                required
              >
                <option value="">Select project</option>
                {projects.map((p) => (
                  <option key={p.Project_Id ?? p.project_id ?? p.id} value={String(p.Project_Id ?? p.project_id ?? p.id)}>
                    {p.Project_Name ?? p.project_name ?? p.name ?? ""}
                  </option>
                ))}
              </select>
            </div>
            {showSubprojectField && (
              <div className="form-group">
                <label>Subproject</label>
                <select
                  name="subProjectId"
                  value={formData.subProjectId}
                  onChange={handleSubprojectChange}
                  disabled={loadingSubprojects}
                >
                  <option value="">Select subproject (optional)</option>
                  {subprojects.map((sp) => (
                    <option key={sp.id} value={String(sp.id)}>{sp.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="form-group">
              <label>User Story <span className="required">*</span></label>
              <select
                name="userStoryId"
                value={formData.userStoryId}
                onChange={handleInputChange}
                disabled={loadingUserStories}
                required
              >
                <option value="">Select user story</option>
                {userStories.map((s) => (
                  <option key={s.id} value={String(s.id)}>{s.title || `#${s.id}`}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Title <span className="required">*</span></label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
              >
                <option value="">Select status</option>
                {taskStatuses.map((s) => (
                  <option key={s.Status_Id ?? s.status_id} value={s.Status ?? s.status}>
                    {s.Status ?? s.status}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Priority</label>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
              >
                <option value="">Select priority</option>
                {TASK_PRIORITIES.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Start Date</label>
              <input
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleInputChange}
                min={today}
              />
            </div>
            <div className="form-group">
              <label>End Date</label>
              <input
                type="date"
                name="endDate"
                value={formData.endDate}
                onChange={handleInputChange}
                min={formData.startDate || today}
              />
            </div>
            <div className="form-group form-group-multi-select">
              <label>Assigned Users</label>
              <div style={{ width: "100%" }}>
                <div
                  className="permissions-select-trigger"
                  onClick={() => {
                    if (!loadingUsers) setShowAssigneesDropdown((prev) => !prev);
                  }}
                  style={{
                    opacity: loadingUsers ? 0.6 : 1,
                    cursor: loadingUsers ? "default" : "pointer"
                  }}
                >
                  <span className="permissions-select-text">
                    {loadingUsers
                      ? "Loading users..."
                      : formData.assignedUserIds.length === 0
                        ? "Select assignees"
                        : `${formData.assignedUserIds.length} user${formData.assignedUserIds.length > 1 ? "s" : ""} selected`}
                  </span>
                  <FaChevronDown
                    className={`permissions-select-chevron ${showAssigneesDropdown ? "open" : ""}`}
                  />
                </div>
                {showAssigneesDropdown && !loadingUsers && (
                  <div
                    className="permissions-select-panel"
                    style={{ maxHeight: 220, overflowY: "auto" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <label className="permissions-select-item">
                      <input
                        type="checkbox"
                        checked={allUsersSelected}
                        onChange={handleToggleSelectAllUsers}
                      />
                      <span className="permissions-select-item-label">Select all</span>
                    </label>
                    {users.map((u) => (
                      <label key={u.id} className="permissions-select-item">
                        <input
                          type="checkbox"
                          checked={formData.assignedUserIds.includes(u.id)}
                          onChange={() => handleUserToggle(u.id)}
                        />
                        <span className="permissions-select-item-label">{u.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="form-group addtask-description-full">
              <label>Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="4"
               
              />
            </div>
            {canAddTaskFiles ? (
              <div className="form-group addtask-description-full">
                <label>Attachments (optional)</label>
                <input
                  type="file"
                  className="issue-files-native-input"
                  disabled={submitting}
                  multiple
                  onChange={(e) => {
                    const next = Array.from(e.target.files || []);
                    setPendingFiles((prev) => [...prev, ...next]);
                    e.target.value = "";
                  }}
                />
                {pendingFiles.length > 0 ? (
                  <ul className="issue-files-pending-list">
                    {pendingFiles.map((f, idx) => (
                      <li key={`${f.name}-${idx}`} className="issue-files-pending-item">
                        <span className="issue-files-pending-name">{f.name}</span>
                        <button
                          type="button"
                          className="issue-files-pending-remove"
                          onClick={() => setPendingFiles((prev) => prev.filter((_, i) => i !== idx))}
                          disabled={submitting}
                          title="Remove"
                        >
                          <FaTimes />
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
        <div className="form-actions edittenant-actions">
          <button type="submit" className="btn-subproject-primary" disabled={submitting}>
            {submitting ? "Creating..." : "Create Task"}
          </button>
        </div>
      </form>
      </div>
    </div>
  );
}

export default AddTask;
